import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import Logo from '../../images/logo.svg';
import arabicBg from '../../images/arabic-bg-dark.svg';
import './style.css';

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:8000');

const SignupVerifyPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [verifying, setVerifying] = useState(true);
    const [verified, setVerified] = useState(false);
    const [error, setError] = useState('');
    const hasVerified = useRef(false);

    useEffect(() => {
        if (hasVerified.current) {
            return;
        }

        const verifyEmail = async () => {
            const token = searchParams.get('token');

            if (!token) {
                setError('Invalid verification link');
                setVerifying(false);
                return;
            }

            hasVerified.current = true;

            try {
                const response = await fetch(`${API_BASE}/api/v1/auth/verify-pending-email`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token })
                });

                const data = await response.json();

                if (data.verified) {
                    setVerified(true);
                    toast.success('Email verified! You can now close this tab or continue signup.');

                    // Store the pending_id in sessionStorage so SignUpPage can resume
                    if (data.pending_id) {
                        const existingSession = sessionStorage.getItem('signup_session');
                        let session = existingSession ? JSON.parse(existingSession) : {};
                        session.pendingUserId = data.pending_id;
                        session.step = 4; // Mark as ready for password step
                        session.verified = true;
                        sessionStorage.setItem('signup_session', JSON.stringify(session));
                        console.log('[SignupVerify] Updated session with verified status:', session);
                    }
                } else {
                    setError(data.message || 'Verification failed');
                }
            } catch (err) {
                console.error('Verification error:', err);
                setError('An error occurred during verification. Please try again.');
            } finally {
                setVerifying(false);
            }
        };

        verifyEmail();
    }, [searchParams]);

    return (
        <div className="verify-email-page">
            {/* Arabic Background Pattern */}
            <div className="verify-email-bg-pattern" style={{ backgroundImage: `url(${arabicBg})` }} />

            {/* Header */}
            <div className="verify-email-header">
                <Link to="/" className="verify-email-logo">
                    <img src={Logo} alt="Anas Logo" />
                </Link>
            </div>

            {/* Main Content */}
            <div className="verify-email-content">
                {verifying && (
                    <div className="verify-email-card">
                        <div className="status-icon-wrapper loading">
                            <FaSpinner className="status-icon spinning" />
                        </div>
                        <h1>Verifying Your Email</h1>
                        <p>Please wait while we verify your email address...</p>
                    </div>
                )}

                {!verifying && verified && (
                    <div className="verify-email-card">
                        <div className="status-icon-wrapper success">
                            <FaCheckCircle className="status-icon" />
                        </div>
                        <h1>Email Verified! 🎉</h1>
                        <p>Your email has been successfully verified.</p>
                        <p className="redirect-text" style={{ marginTop: '15px', color: '#666', fontSize: '14px' }}>
                            Return to the signup page to set your password and complete registration.
                        </p>
                        <div className="verify-actions" style={{ marginTop: '20px' }}>
                            <button
                                onClick={() => navigate('/signup')}
                                className="btn-primary"
                                style={{
                                    background: 'linear-gradient(135deg, #ffb100 0%, #ff9f00 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '15px 40px',
                                    borderRadius: '10px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Continue to Signup
                            </button>
                        </div>
                    </div>
                )}

                {!verifying && error && (
                    <div className="verify-email-card">
                        <div className="status-icon-wrapper error">
                            <FaTimesCircle className="status-icon" />
                        </div>
                        <h1>Verification Failed</h1>
                        <p className="error-message">{error}</p>
                        <div className="verify-actions" style={{ marginTop: '20px' }}>
                            <button
                                onClick={() => navigate('/signup')}
                                className="btn-primary"
                                style={{
                                    background: 'linear-gradient(135deg, #004585 0%, #062265 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '15px 40px',
                                    borderRadius: '10px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Back to Signup
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SignupVerifyPage;
